import onReady from '../utils/onReady';
import domIterator from '../utils/domIterator';
import * as ClassList from '../utils/classList';
import * as Events from '../utils/events';
import Flickity from 'flickity';
import {currentBreakpoint,
        default as onWindowResize
        } from '../utils/responsiveWindow';

/**
 * Boilerplate class demonstrating basic construction.
 * You can expand from here, or find verbose examples in example.js.
 *
 * @param {HTMLElement} container
 * @constructor
 */
class Gallery {

    /**
     * The constructor is fired once the class is instantiated.
     *
     * @param {HTMLElement} container
     */
    constructor(container) {
        // Run initializing code once the DOM is ready.
        onReady(() => this.init(container));

        // You can put other initializer-code here if it isn't dependent on the DOM in any way.
    }

    makeGallery() {
        this.gallery = new Flickity(this.dom.carouselMain, this.settings.carouselMain);

        // Set inline style of carouselMain, for safari
        this.lastBreakPoint = currentBreakpoint;
        this.dom.carouselMain.style.height = this.dom.carouselMain.offsetHeight + "px";

        this.stopPlay();
    }

    addEvents() {
        Events.addEventOnce(this.dom.container, "click", () => {
            ClassList.add(this.dom.container, "carousel-container--active");
            ClassList.add(this.dom.playButton, "textlink--reverse");
        });

        domIterator(this.dom.navButtons, (element, index) => {
            Events.addEvent(element, "click", () => {
                this.gallery.select( index, true, false);
                this.stopPlay();
            });
        });

        Events.addEvent(this.dom.nextButton, "click", event => {
            event.preventDefault();
            this.incrementPage();
            this.showPage();
        });

        // Start slideshow if play button clicked
        Events.addEvent(this.dom.playButton, "click", event => {
            event.preventDefault();

            if (this.isPlaying) {
                this.stopPlay();
            } else {
                this.page = Math.floor(this.gallery.selectedIndex / this.imagePerPage);
                this.showPage();
                // this.gallery.select(1, true, false);
                this.startPlay();
            }

        });

        // Listen for selects, and see if current select is higher than imagePerPage - 1 (zero index), to change thumbnail nav.
        this.gallery.on("select", () => {

            // Return if event triggered by click, instead of autoplay
            if (!this.isPlaying) {
                return;
            }

            let imageIndex = this.gallery.selectedIndex,
                pageCounter = ((this.imagePerPage * (this.page + 1)) - 1);

            if (imageIndex > pageCounter || imageIndex === 0) {
                this.incrementPage();
                this.showPage();
            }

        });

        this.gallery.on("dragStart", () => {
            this.isPlaying = false;
        });
    }

    startPlay() {
        this.dom.playButtonText.innerHTML = "Stop slideshow";
        ClassList.add(this.dom.playButton, "carousel__playButton--playing");
        this.gallery.playPlayer();
        this.isPlaying = true;
    }

    stopPlay() {
        this.dom.playButtonText.innerHTML = "Start slideshow";
        ClassList.remove(this.dom.playButton, "carousel__playButton--playing");
        this.gallery.stopPlayer();
        this.isPlaying = false;
    }

    incrementPage() {
        this.page += 1;

        if (this.page > this.pageMax) {
            this.page = 0;
        }
    }

    showPage() {
        let translateAmount = ((this.imageHeight + this.imageMargin) * this.imagePerPage) * this.page;
        this.dom.navCellSlider.style.transform = "translate(0, -" + translateAmount + "px)";
    }

    resizeHasHappened() {
        let curImageHeight = this.dom.navButtons[0].offsetHeight;

        // If height has changed due to css media queries, redo the position of the slider
        if (curImageHeight !== this.imageHeight) {
            this.imageHeight = curImageHeight;
            this.showPage();
        }


        // Set inline style of carouselMain if its different than last known height. For safari
        // If breakpoint is below 3/sm, we have to apply on each resize
        if (this.lastBreakPoint !== currentBreakpoint || currentBreakpoint < 3) {

            // Remove current height styling, so the natural height applies
            this.dom.carouselMain.style.height = "";
            this.dom.carouselMain.style.height = this.dom.carouselMain.offsetHeight + "px";
            this.lastBreakPoint = currentBreakpoint;
        }
    }


    /**
     * The actual initialization function, fired once the DOM is ready.
     *
     * @param {HTMLElement} container
     */
    init(container) {

        this.dom = {
            container // ES6 allows this syntax instead of writing "container: container"
        };

        this.settings = {
            carouselMain: {
                // Options
                "wrapAround": true,
                "setGallerySize": true, // Set container size equal to the highest image
                "imagesLoaded": true, // Wait for images to be loaded
                "pageDots": true,
                "autoplay": 2000,
                arrowShape: {
                    x0: 30,
                    x1: 55, y1: 25,
                    x2: 60, y2: 20,
                    x3: 40
                }
            }
        };

        // Put your other DOM-specific initializations here, and get your class moving!

        this.dom.carouselMain = this.dom.container.querySelector('.carousel-main');
        this.dom.carouselNav = this.dom.container.querySelector('.carousel-nav');
        this.dom.navCellContainer = this.dom.carouselNav.querySelector('.nav-cell-container');
        this.dom.navCellSlider = this.dom.navCellContainer.querySelector('.nav-cell-slider');
        this.dom.navButtons = this.dom.navCellSlider.querySelectorAll('.carousel-nav-cell');
        this.dom.nextButton = this.dom.carouselNav.querySelector('.carousel__nextButton');
        this.dom.playButton = this.dom.container.querySelector('.carousel__playButton');
        this.dom.playButtonText = this.dom.playButton.querySelector('.textlink__text');

        if (!this.dom.navButtons.length) {
            return;
        }

        this.imageHeight = this.dom.navButtons[0].offsetHeight;
        this.imageMargin = 20;
        this.imageCount = this.dom.navButtons.length;

        // To keep track of which "page" of thumbnails is currently showing
        this.page = 0;
        this.imagePerPage = 3;

        // Max page count. (zero-indexed)
        this.pageMax = (this.imageCount % this.imagePerPage) === 0 ? (this.imageCount / this.imagePerPage) - 1 : Math.floor(this.imageCount / this.imagePerPage);

        this.makeGallery();
        this.addEvents();

        // Fire once to make sure Flickity has right dimensions on mobile
        this.resizeHasHappened();

        // Check for resize
        onWindowResize(this.resizeHasHappened.bind(this));
    }

}


export default Gallery;